import { startLoading, stopLoading } from "@slices/loader.slice";
import { store } from "@store";
import axios, {AxiosError, AxiosRequestConfig} from "axios";
import { ApplicationResponse } from "../domain/common.model";
import { showErrorToast, showSuccessToast } from "@utils/toast.utils";
import appConstants from "@src/environment/app.constants";
import {BaseQueryFn} from "@reduxjs/toolkit/query/react";

const api = axios.create({
    baseURL: appConstants.api
});

api.interceptors.request.use(
  (config) => {
    store.dispatch(startLoading());
    return config;
  },
  (error) => {
    store.dispatch(stopLoading());
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    store.dispatch(stopLoading());
    const params = response.config.params;
    const data = response.data as ApplicationResponse<any>;
    if (
      data.status === "RESOURCE_CREATED" ||
      data.status === "RESOURCE_UPDATED" ||
      data.status === "RESOURCE_DELETED"
    ) {
      showSuccessToast(data.message);
      if (params?.RAR !== 0) { //TODO - Make changes in this so that window do not reload if there is no parameter named RAR.
          console.log("Window reloaded...")
        window.location.reload();
      }
    }
    if (data.status === "ERROR") {
      showErrorToast(data.message);
    }
    return response;
  },
  (error) => {
    store.dispatch(stopLoading());
    return Promise.reject(error);
  }
);

export const setupAxiosInterceptors = (store: any) => {
  api.interceptors.request.use(
    (config) => {
      const state = store.getState();
      const token = state.auth.token.data; // Assuming this is where your token is stored

      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

export default api;

type AxiosBaseQueryArgs = {
    url: string;
    method: AxiosRequestConfig["method"];
    data?: AxiosRequestConfig["data"];
    params?: AxiosRequestConfig["params"];
    headers?: AxiosRequestConfig["headers"];
}

const customBaseQuery =
    (): BaseQueryFn<AxiosBaseQueryArgs, unknown, unknown> =>
        async ({ url, method, data, params, headers }) => {
            try {
                const response = await api({
                    url,
                    method,
                    data,
                    params,
                    headers,
                });

                // Optionally unwrap the `ApplicationResponse` if all your APIs follow it
                const appResponse = response.data as ApplicationResponse<any>;

                if (appResponse.status === 'ERROR') {
                    return {
                        error: {
                            status: 400,
                            data: appResponse.message,
                        },
                    };
                }

                return { data: appResponse.data ?? appResponse }; // allow both
            } catch (err) {
                const error = err as AxiosError;
                return {
                    error: {
                        status: error.response?.status ?? 500,
                        data: error.response?.data ?? error.message,
                    },
                };
            }
        };
export { customBaseQuery };
